<script setup lang="ts">
    import { ref, computed } from 'vue';
    import { useRouter } from 'vue-router';
    import { requireCanCreateLibrary } from '@/shared/directives/requirePermission';
    import { getLibraryLink } from '@/shared/helpers/routing';
    import { newRelationLibrary, getLibraryList, LibraryListItem } from '@/shared/helpers/api';
    import LibrarySelector from '@/shared/components/LibrarySelector.vue';
    import ModalBase from '@/shared/components/ModalBase.vue';
    import { http } from '@/shared/httpWrapper';
    import useShow from '@/shared/composables/useShow';
    import { BField } from 'buefy';

    type ValidationField
        = 'codeRefName1'
            | 'codeRefName2'
            | 'libraryName'
            | 'treatAsAttributeLabel1'
            | 'treatAsAttributeLabel2';

    const vRequireCanCreateLibrary = requireCanCreateLibrary;

    const props = defineProps<{
        libraryName?: string;
        libraries?: LibraryListItem[];
    }>();

    const libraries = ref<LibraryListItem[]>(props.libraries ?? []);

    const router = useRouter();
    const show = useShow();

    const isModalActive = ref<boolean>(false);
    const relationLibraryName = ref<string>('');
    const selectedLibrary1 = ref<string>('');
    const selectedLibrary2 = ref<string>('');
    const codeRefName1 = ref<string>('');
    const codeRefName2 = ref<string>('');
    const hasTyped = ref<boolean>(false);
    const hasTypedCodeRefName1 = ref<boolean>(false);
    const hasTypedCodeRefName2 = ref<boolean>(false);
    const treatAsAttribute1 = ref<boolean>(false);
    const treatAsAttribute2 = ref<boolean>(false);
    const treatAsAttributeLabel1 = ref<string>('');
    const treatAsAttributeLabel2 = ref<string>('');
    const hasTypedLabel1 = ref<boolean>(false);
    const hasTypedLabel2 = ref<boolean>(false);

    const library1Object = computed<LibraryListItem | undefined>(() => {
        return libraries.value.find(lib => lib.name === selectedLibrary1.value);
    });

    const library2Object = computed<LibraryListItem | undefined>(() => {
        return libraries.value.find(lib => lib.name === selectedLibrary2.value);
    });

    const generatedLibraryName = computed<string>(() => {
        if (selectedLibrary1.value && selectedLibrary2.value) {
            return `${selectedLibrary1.value}_${selectedLibrary2.value}`;
        }
        return '';
    });

    const incompatibleLibrariesSelector1 = computed<string[]>(() => {
        const excluded = getIncompatibleLibraries(library2Object.value);
        if (selectedLibrary2.value) excluded.push(selectedLibrary2.value);
        return excluded;
    });

    const incompatibleLibrariesSelector2 = computed<string[]>(() => {
        const excluded = getIncompatibleLibraries(library1Object.value);
        if (selectedLibrary1.value) excluded.push(selectedLibrary1.value);
        return excluded;
    });

    const selectedLibraries = computed(() => ({
        lib1: library1Object.value,
        lib2: library2Object.value
    }));

    const newLibraryScopeType = computed<{ isGlobal: boolean; scopeType: string | null } | null>(() => {
        const { lib1, lib2 } = selectedLibraries.value;

        if (!lib1 || !lib2) return null;

        if (lib1.isGlobal && lib2.isGlobal) {
            return { isGlobal: true, scopeType: null };
        }

        const scopeType = lib1.isGlobal ? lib2.scopeType : lib1.scopeType;
        return { isGlobal: false, scopeType };
    });

    const scopeFeedbackMessage = computed<string>(() => {
        const { lib1, lib2 } = selectedLibraries.value;

        if (!lib1 && !lib2) return '';

        if (lib1 && !lib2) {
            return lib1.isGlobal
                ? 'First library is global. Relation library scope will depend on second library selection.'
                : `First library is scoped on ${lib1.scopeType}. Second library must be global or also scoped on ${lib1.scopeType}.`;
        }

        if (!lib1 && lib2) {
            return lib2.isGlobal
                ? 'Second library is global. Relation library scope will depend on first library selection.'
                : `Second library is scoped on ${lib2.scopeType}. First library must be global or also scoped on ${lib2.scopeType}.`;
        }

        const scopeInfo = newLibraryScopeType.value;
        return scopeInfo?.isGlobal
            ? 'Relation library will be global.'
            : `Relation library will be scoped on ${scopeInfo?.scopeType}.`;
    });

    const namePattern = /^[a-zA-Z]([a-zA-Z0-9_]){2,63}$/;

    const areCodeRefNamesUnique = computed<boolean>(() => {
        const name1 = codeRefName1.value.trim().toLowerCase();
        const name2 = codeRefName2.value.trim().toLowerCase();
        if (!name1 || !name2) return true;
        return name1 !== name2;
    });

    const isValid = computed<boolean>(() =>
        !!selectedLibrary1.value
        && !!selectedLibrary2.value
        && namePattern.test(relationLibraryName.value.trim())
        && namePattern.test(codeRefName1.value.trim())
        && namePattern.test(codeRefName2.value.trim())
        && areCodeRefNamesUnique.value
        && (!treatAsAttribute1.value || namePattern.test(treatAsAttributeLabel1.value.trim()))
        && (!treatAsAttribute2.value || namePattern.test(treatAsAttributeLabel2.value.trim()))
    );

    function validateName(value: string): string {
        const name = value.trim();
        if (!name) return 'This field is required';
        if (!/^[a-zA-Z]/.test(name)) return 'Must start with a letter';
        if (name.length > 64) return 'Must be at most 64 characters';
        if (name.length < 3) return 'Must be at least 3 characters';
        if (!namePattern.test(name)) return 'Can only contain letters, numbers and underscores';
        return '';
    }

    function getIncompatibleLibraries(otherLibraryObject?: LibraryListItem): string[] {
        const excluded: string[] = [];

        if (otherLibraryObject && !otherLibraryObject.isGlobal) {
            const scopeType = otherLibraryObject.scopeType;
            libraries.value.forEach(lib => {
                if (!lib.isGlobal && lib.scopeType !== scopeType) {
                    excluded.push(lib.name);
                }
            });
        }
        return excluded;
    }

    async function openModal(): Promise<void> {
        relationLibraryName.value = '';
        selectedLibrary1.value = '';
        selectedLibrary2.value = '';
        codeRefName1.value = '';
        codeRefName2.value = '';
        hasTyped.value = false;
        hasTypedCodeRefName1.value = false;
        hasTypedCodeRefName2.value = false;
        treatAsAttribute1.value = false;
        treatAsAttribute2.value = false;
        treatAsAttributeLabel1.value = '';
        treatAsAttributeLabel2.value = '';
        hasTypedLabel1.value = false;
        hasTypedLabel2.value = false;

        if (props.libraries) {
            libraries.value = props.libraries;
        } else {
            try {
                libraries.value = await getLibraryList(http);
            } catch {
                libraries.value = [];
            }
        }

        if (props.libraryName) {
            selectedLibrary1.value = props.libraryName;
            codeRefName1.value = props.libraryName;
            treatAsAttributeLabel1.value = props.libraryName;
        }

        isModalActive.value = true;
    }

    async function onConfirm(): Promise<void> {
        await makeNewLibrary(relationLibraryName.value.trim());
    }

    async function makeNewLibrary(libName: string): Promise<void> {
        try {
            const scopeInfo = newLibraryScopeType.value;

            await newRelationLibrary(http, {
                name: libName,
                description: `Relates ${selectedLibrary1.value} to ${selectedLibrary2.value}`,
                isGlobal: scopeInfo?.isGlobal ?? false,
                ...(scopeInfo?.scopeType ? { scopeType: scopeInfo.scopeType } : {}),
                referenceLibraries: [
                    {
                        libraryName: selectedLibrary1.value,
                        codeRefName: codeRefName1.value.trim(),
                        treatAsAttribute: treatAsAttribute1.value,
                        treatAsAttributeLabel: treatAsAttribute1.value ? (treatAsAttributeLabel1.value.trim() || null) : null
                    },
                    {
                        libraryName: selectedLibrary2.value,
                        codeRefName: codeRefName2.value.trim(),
                        treatAsAttribute: treatAsAttribute2.value,
                        treatAsAttributeLabel: treatAsAttribute2.value ? (treatAsAttributeLabel2.value.trim() || null) : null
                    }
                ]
            });

            isModalActive.value = false;
            await router.push(getLibraryLink(libName));
        } catch (error) {
            show.showError(error);
        }
    }

    function onLibrarySelected(library: '1' | '2', payload: string): void {
        switch (library) {
            case '1':
                selectedLibrary1.value = payload;
                codeRefName1.value = payload;
                treatAsAttributeLabel1.value = payload;
                break;
            case '2':
                selectedLibrary2.value = payload;
                codeRefName2.value = payload;
                treatAsAttributeLabel2.value = payload;
                break;
        }
        relationLibraryName.value = generatedLibraryName.value;
    }

    function codeRefNameFeedback(selectedLibrary: string, hasTypedCodeRefName: boolean, value: string): string | undefined {
        const feedback = validateName(value);
        if ((selectedLibrary || hasTypedCodeRefName) && feedback) {
            return feedback;
        }
        if (!areCodeRefNamesUnique.value) {
            return 'CodeRef names must be unique';
        }
        return undefined;
    }

    function validationFeedbackMessage(field: ValidationField): string | undefined {
        switch (field) {
            case 'codeRefName1':
                return codeRefNameFeedback(
                    selectedLibrary1.value,
                    hasTypedCodeRefName1.value,
                    codeRefName1.value
                );
            case 'codeRefName2':
                return codeRefNameFeedback(
                    selectedLibrary2.value,
                    hasTypedCodeRefName2.value,
                    codeRefName2.value
                );
            case 'treatAsAttributeLabel1': {
                const feedback = validateName(treatAsAttributeLabel1.value);
                if ((hasTypedLabel1.value || treatAsAttributeLabel1.value) && feedback) {
                    return feedback;
                }
                break;
            }
            case 'treatAsAttributeLabel2': {
                const feedback = validateName(treatAsAttributeLabel2.value);
                if ((hasTypedLabel2.value || treatAsAttributeLabel2.value) && feedback) {
                    return feedback;
                }
                break;
            }
            case 'libraryName': {
                const feedback = validateName(relationLibraryName.value);
                if ((hasTyped.value || relationLibraryName.value) && feedback) {
                    return feedback;
                }
                break;
            }
        }
        return undefined;
    }
</script>

<template>
    <div
        v-require-can-create-library>
        <button
            class="button is-info"
            @click="openModal">
            <span class="icon is-small">
                <i
                    class="mdi mdi-relation-many-to-many"
                    aria-hidden="true" />
            </span>
            <span>New relation</span>
        </button>

        <modal-base
            v-model="isModalActive"
            @onCancel="isModalActive = false"
            @onSave="onConfirm">
            <template #modal-header>
                Create library relation
            </template>

            <template #modal-body>
                <b-field class="mb-1">
                    <template #label>
                        <span class="">First library:</span>
                        <span class=" has-text-danger">*</span>
                    </template>
                    <library-selector
                        :selected="selectedLibrary1"
                        :existing-libraries="incompatibleLibrariesSelector1"
                        @lib-selected="(payload: string) => onLibrarySelected('1', payload)" />
                </b-field>
                <p class="has-text-grey-light single-line mb-3">
                    <span v-if="library1Object">
                        {{ library1Object.isGlobal ? 'Scope: Global' : `Scope: ${library1Object.scopeType}` }}
                    </span>
                </p>

                <b-field
                    :message="validationFeedbackMessage('codeRefName1')"
                    :type="validationFeedbackMessage('codeRefName1') ? 'is-danger' : undefined">
                    <template #label>
                        <span>First library attribute name:</span>
                        <span class="has-text-danger">*</span>
                    </template>
                    <b-input
                        v-model="codeRefName1"
                        minlength="1"
                        maxlength="64"
                        @input="hasTypedCodeRefName1 = true" />
                </b-field>

                <b-field class="mb-5">
                    <b-checkbox v-model="treatAsAttribute1">
                        Display items of {{ selectedLibrary1 || 'first library' }} on {{ selectedLibrary2 || 'second library' }}?
                    </b-checkbox>
                </b-field>
                <b-field
                    v-if="treatAsAttribute1"
                    :message="validationFeedbackMessage('treatAsAttributeLabel1')"
                    :type="validationFeedbackMessage('treatAsAttributeLabel1') ? 'is-danger' : undefined">
                    <template #label>
                        <span>Label</span>
                    </template>
                    <b-input
                        v-model="treatAsAttributeLabel1"
                        minlength="3"
                        maxlength="64"
                        @input="hasTypedLabel1 = true" />
                </b-field>

                <hr class="my-5 has-background-grey-lighter">
                <b-field class="mb-1">
                    <template #label>
                        <span>Second library:</span>
                        <span class="has-text-danger">*</span>
                    </template>
                    <library-selector
                        :selected="selectedLibrary2"
                        :existing-libraries="incompatibleLibrariesSelector2"
                        @lib-selected="(payload: string) => onLibrarySelected('2', payload)" />
                </b-field>
                <p class=" has-text-grey-light single-line mb-3">
                    <span v-if="library2Object">
                        {{ library2Object.isGlobal ? 'Scope: Global' : `Scope: ${library2Object.scopeType}` }}
                    </span>
                </p>

                <b-field
                    :message="validationFeedbackMessage('codeRefName2')"
                    :type="validationFeedbackMessage('codeRefName2') ? 'is-danger' : undefined">
                    <template #label>
                        <span>Second library attribute name:</span>
                        <span class="has-text-danger">*</span>
                    </template>
                    <b-input
                        v-model="codeRefName2"
                        minlength="3"
                        maxlength="64"
                        @input="hasTypedCodeRefName2 = true" />
                </b-field>
                <b-field class="mb-4">
                    <b-checkbox v-model="treatAsAttribute2">
                        Display items of {{ selectedLibrary2 || 'second library' }} on {{ selectedLibrary1 || 'first library' }}?
                    </b-checkbox>
                </b-field>
                <b-field
                    v-if="treatAsAttribute2"
                    :message="validationFeedbackMessage('treatAsAttributeLabel2')"
                    :type="validationFeedbackMessage('treatAsAttributeLabel2') ? 'is-danger' : undefined">
                    <template #label>
                        <span>Label</span>
                    </template>
                    <b-input
                        v-model="treatAsAttributeLabel2"
                        minlength="3"
                        maxlength="64"
                        @input="hasTypedLabel2 = true" />
                </b-field>

                <hr class="my-5 has-background-grey-lighter">

                <b-field
                    class="mb-4"
                    :message="validationFeedbackMessage('libraryName')"
                    :type="validationFeedbackMessage('libraryName') ? 'is-danger' : undefined">
                    <template #label>
                        <span>Relation library name (3-64 characters allowed, must start with a letter):</span>
                        <span class="has-text-danger">*</span>
                    </template>
                    <b-input
                        v-model="relationLibraryName"
                        :placeholder="selectedLibrary1 && selectedLibrary2 ? '' : 'Select both libraries to generate name'"
                        @input="hasTyped = true" />
                </b-field>
            </template>

            <template #modal-footer>
                <div class="is-flex is-justify-content-space-between is-full-width">
                    <p
                        v-if="scopeFeedbackMessage"
                        class="has-text-info scope-feedback">
                        {{ scopeFeedbackMessage }}
                    </p>
                    <div v-else />

                    <div class="buttons">
                        <b-button
                            label="Cancel"
                            @click="isModalActive = false" />
                        <b-button
                            label="OK"
                            type="is-primary"
                            class="ml-3"
                            :disabled="!isValid"
                            @click="onConfirm" />
                    </div>
                </div>
            </template>
        </modal-base>
    </div>
</template>

<style scoped>
    .scope-feedback {
        max-width: 60%;
    }

    .single-line {
        min-height: 1lh;
    }
</style>
